// src/taskpane/services/textExtractor.ts
import { ExtractedTextItem, ExtractionResult } from "./types";

/** 调试日志收集器 */
let debugLogs: string[] = [];

export function getDebugLogs(): string[] {
  return debugLogs;
}

export function clearDebugLogs(): void {
  debugLogs = [];
}

/** 从单个形状中提取文本（通用方法） */
async function extractFromShape(
  shape: PowerPoint.Shape,
  slideIndex: number,
  shapeIndex: number,
  items: ExtractedTextItem[],
  isGroupChild: boolean = false
): Promise<void> {
  const shapeType = shape.type;
  const shapeName = shape.name || "(unnamed)";
  let shapeTypeName = "unknown";

  try {
    shapeTypeName = PowerPoint.ShapeType[shapeType] || String(shapeType);
  } catch {
    shapeTypeName = String(shapeType);
  }

  debugLogs.push(`[Slide ${slideIndex}] #${shapeIndex} 形状: "${shapeName}", 类型: ${shapeTypeName}`);

  // 处理组合形状：递归遍历子形状
  if (shapeType === PowerPoint.ShapeType.group) {
    try {
      const group = shape.group;
      group.shapes.load("items/type, items/name");
      await group.context.sync();
      debugLogs.push(`  → 组合形状，包含 ${group.shapes.items.length} 个子形状`);

      for (let ci = 0; ci < group.shapes.items.length; ci++) {
        await extractFromShape(group.shapes.items[ci], slideIndex, ci, items, true);
      }
    } catch (e) {
      debugLogs.push(`  → 组合形状访问失败: ${e}`);
    }
    return;
  }

  // 处理表格
  if (shapeType === PowerPoint.ShapeType.table) {
    try {
      const table = shape.getTable();
      table.load("rowCount, columnCount");
      await table.context.sync();

      for (let r = 0; r < table.rowCount; r++) {
        for (let c = 0; c < table.columnCount; c++) {
          const cell = table.getCellOrNullObject(r, c);
          cell.load("isNullObject, text");
          await cell.context.sync();

          if (!cell.isNullObject && cell.text.trim()) {
            items.push({
              id: `${slideIndex}-${shapeIndex}-r${r}c${c}`,
              slideIndex,
              shapeIndex,
              shapeName,
              shapeType: "table",
              tableRowIndex: r,
              tableColIndex: c,
              originalText: cell.text,
              translatedText: "",
              translated: false,
              error: null,
            });
          }
        }
      }
      debugLogs.push(`  → 表格: ${table.rowCount}x${table.columnCount}`);
    } catch (e) {
      debugLogs.push(`  → 表格访问失败: ${e}`);
    }
    return;
  }

  // 跳过明显不包含文本的形状类型
  if (
    shapeType === PowerPoint.ShapeType.image ||
    shapeType === PowerPoint.ShapeType.video ||
    shapeType === PowerPoint.ShapeType.onlineVideo ||
    shapeType === PowerPoint.ShapeType.picture ||
    shapeType === PowerPoint.ShapeType.media ||
    shapeType === PowerPoint.ShapeType.webVideo ||
    shapeType === PowerPoint.ShapeType.smartArt ||
    shapeType === PowerPoint.ShapeType.chart
  ) {
    debugLogs.push(`  → 跳过（非文本类型）`);
    return;
  }

  // 对所有其他形状类型，统一尝试提取 TextFrame
  try {
    const textFrame = shape.getTextFrameOrNullObject();
    textFrame.load("isNullObject, hasText, textRange/text");
    await textFrame.context.sync();

    debugLogs.push(`  → TextFrame: isNull=${textFrame.isNullObject}, hasText=${textFrame.hasText}`);

    if (!textFrame.isNullObject && textFrame.hasText) {
      const text = textFrame.textRange.text;
      debugLogs.push(`  → 文本内容: "${text.substring(0, 50)}${text.length > 50 ? "..." : ""}"`);

      if (text.trim()) {
        items.push({
          id: `${slideIndex}-${shapeIndex}`,
          slideIndex,
          shapeIndex,
          shapeName,
          shapeType: isGroupChild
            ? "group-child"
            : shapeType === PowerPoint.ShapeType.placeholder
            ? "placeholder"
            : shapeType === PowerPoint.ShapeType.textBox
            ? "textbox"
            : "shape",
          tableRowIndex: null,
          tableColIndex: null,
          originalText: text,
          translatedText: "",
          translated: false,
          error: null,
        });
      }
    }
  } catch (e) {
    debugLogs.push(`  → TextFrame 访问失败: ${e}`);
  }
}

/** 处理一张幻灯片中的所有形状 */
async function extractFromSlide(
  slide: PowerPoint.Slide,
  slideIndex: number,
  items: ExtractedTextItem[]
): Promise<void> {
  const shapes = slide.shapes;
  shapes.load("items/type, items/name");
  await slide.context.sync();

  debugLogs.push(`--- 幻灯片 ${slideIndex + 1}: ${shapes.items.length} 个形状 ---`);

  for (let si = 0; si < shapes.items.length; si++) {
    await extractFromShape(shapes.items[si], slideIndex, si, items);
  }
}

/**
 * 从当前演示文稿中提取所有文本内容
 */
export async function extractAllText(): Promise<ExtractionResult> {
  const items: ExtractedTextItem[] = [];
  debugLogs = [];

  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();

    debugLogs.push(`=== 全部扫描，共 ${slides.items.length} 张幻灯片 ===`);

    for (let i = 0; i < slides.items.length; i++) {
      await extractFromSlide(slides.items[i], i, items);
    }

    debugLogs.push(`=== 扫描完成，提取 ${items.length} 个文本项 ===`);
  });

  return buildResult(items);
}

/**
 * 从当前幻灯片中提取所有文本内容
 */
export async function extractCurrentSlideText(): Promise<ExtractionResult> {
  const items: ExtractedTextItem[] = [];
  debugLogs = [];

  await PowerPoint.run(async (context) => {
    const slide = context.presentation.getSelectedSlides().getItemAt(0);
    const slideIndex = await getSlideIndex(slide);
    debugLogs.push(`=== 当前页扫描 (幻灯片 ${slideIndex + 1}) ===`);

    await extractFromSlide(slide, slideIndex, items);

    debugLogs.push(`=== 扫描完成，提取 ${items.length} 个文本项 ===`);
  });

  return buildResult(items);
}

/**
 * 提取当前选中的形状中的文本
 * 如果选中了多个形状，全部提取
 * 如果选中的是组合形状，递归提取子形状
 */
export async function extractSelectedShapeText(): Promise<ExtractionResult> {
  const items: ExtractedTextItem[] = [];
  debugLogs = [];

  await PowerPoint.run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items");
    await context.sync();

    debugLogs.push(`=== 选中形状扫描，共 ${selected.items.length} 个形状 ===`);

    // 获取当前幻灯片索引
    const slide = context.presentation.getSelectedSlides().getItemAt(0);
    const slideIndex = await getSlideIndex(slide);

    // 获取当前幻灯片的所有形状，用于匹配真实索引
    const allShapes = slide.shapes;
    allShapes.load("items/id, items/name");
    await context.sync();

    for (let si = 0; si < selected.items.length; si++) {
      const selectedShape = selected.items[si];
      selectedShape.load("id, name");
      await context.sync();

      // 在幻灯片 shapes 集合中找到该形状的真实索引
      let realIndex = -1;
      for (let ai = 0; ai < allShapes.items.length; ai++) {
        allShapes.items[ai].load("id");
      }
      await context.sync();

      for (let ai = 0; ai < allShapes.items.length; ai++) {
        if (allShapes.items[ai].id === selectedShape.id) {
          realIndex = ai;
          break;
        }
      }

      if (realIndex === -1) {
        debugLogs.push(`  ⚠️ 未在幻灯片中找到选中形状 "${selectedShape.name}"`);
        realIndex = si; // 回退
      }

      debugLogs.push(`  → 选中形状 "${selectedShape.name}" 在幻灯片中的索引: ${realIndex}`);
      await extractFromShape(selectedShape, slideIndex, realIndex, items);
    }

    debugLogs.push(`=== 扫描完成，提取 ${items.length} 个文本项 ===`);
  });

  return buildResult(items);
}

/** 获取幻灯片在演示文稿中的索引 */
async function getSlideIndex(slide: PowerPoint.Slide): Promise<number> {
  const slides = slide.context.presentation.slides;
  slides.load("items/id");
  slide.load("id");
  await slide.context.sync();

  const slideId = slide.id;
  for (let i = 0; i < slides.items.length; i++) {
    slides.items[i].load("id");
  }
  await slide.context.sync();

  for (let i = 0; i < slides.items.length; i++) {
    if (slides.items[i].id === slideId) {
      return i;
    }
  }
  return 0;
}

/** 构建统一的提取结果 */
function buildResult(items: ExtractedTextItem[]): ExtractionResult {
  const totalCharacters = items.reduce(
    (sum, item) => sum + item.originalText.length,
    0
  );

  return {
    items,
    totalSlides:
      items.length > 0 ? Math.max(...items.map((i) => i.slideIndex)) + 1 : 0,
    totalTextItems: items.length,
    totalCharacters,
  };
}
